import { api } from '../api.js';
import { state } from '../state.js';
import { navigate } from '../router.js';
import { humanDate, humanDuration } from '../ui.js';
import { loadUserData } from '../bootstrap.js';

// Страница администратора: кто есть кто и что у них происходило.
//
// Смысл её в одном: разбирать жалобы предметно. «Собес оборвался» проверяется
// не расспросами, а тем, что админ заходит в этот аккаунт и слушает тот самый
// разговор. Поэтому здесь минимум своих экранов — из списка сразу попадаешь в
// нужную запись, дальше работают обычные страницы.

export const Admin = {
  data() {
    return { users: [], sessions: [], loading: true, error: '', busy: 0, only: 0 };
  },
  computed: {
    admin() { return state.user && state.user.is_admin; },
  },
  async created() { await this.load(); },
  methods: {
    humanDate,
    humanDuration,

    async load() {
      this.loading = true;
      try {
        const [users, sessions] = await Promise.all([
          api.get('/api/admin/users'),
          api.get('/api/admin/sessions' + (this.only ? `?user=${this.only}` : '')),
        ]);
        this.users = users.rows;
        this.sessions = sessions.rows;
        this.error = '';
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },

    async filter(userId) {
      this.only = this.only === userId ? 0 : userId;
      await this.load();
    },

    /**
     * Войти в аккаунт. Дальше это обычная работа под его именем: проекты,
     * собесы, записи — всё своё. Наверху висит полоса, что это не свой аккаунт.
     */
    async enter(userId, to = '/projects') {
      this.busy = userId;
      try {
        await api.post(`/api/admin/impersonate/${userId}`, {});
        const s = await api.get('/api/auth/state');
        state.user = s.user;
        state.impersonating = Boolean(s.impersonating);
        state.activeSession = s.activeSession;
        await loadUserData({ force: true });
        navigate(to);
      } catch (e) {
        this.error = e.message;
      } finally {
        this.busy = 0;
      }
    },

    kindWord(kind) {
      return ({
        technical: 'технический', drill: 'тренажёр', incident: 'инцидент',
        review: 'ревью', pressure: 'под давлением',
      })[kind] || kind || '—';
    },
  },
  template: `
    <div class="page admin" :data-ready="!loading">
      <h1>Администратор</h1>
      <p class="err" v-if="error">{{ error }}</p>
      <p class="err" v-else-if="!admin">Эта страница только для администратора.</p>

      <template v-else>
        <h2>Аккаунты</h2>
        <div class="card" style="padding:0">
          <table class="rows">
            <thead>
              <tr>
                <th>Аккаунт</th><th>Проектов</th><th>Собесов</th>
                <th>Последний</th><th>Агент</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in users" :key="u.id" :class="{ on: only === u.id }">
                <td>
                  <b>{{ u.login }}</b>
                  <span class="tag ok" v-if="u.is_admin" style="margin-left:6px">админ</span>
                </td>
                <td>{{ u.projects }}</td>
                <td>
                  <a href="#" @click.prevent="filter(u.id)"
                     :title="only === u.id ? 'показать всех' : 'показать только его собесы'">
                    {{ u.sessions }}
                  </a>
                </td>
                <td class="faint">{{ u.last_session ? humanDate(u.last_session) : '—' }}</td>
                <td>
                  <span class="chk" :class="u.online ? 'up' : 'down'"><i></i>
                    {{ u.agent_place === 'server' ? 'на сервере' : 'на компьютере' }}
                  </span>
                </td>
                <td style="text-align:right">
                  <button class="ghost sm" v-if="!u.is_admin"
                          :disabled="busy === u.id" @click="enter(u.id)">
                    {{ busy === u.id ? 'Захожу…' : 'Войти' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2>
          Собеседования
          <span class="faint" v-if="only">— только {{ users.find((u) => u.id === only)?.login }}</span>
        </h2>
        <div class="card" style="padding:0">
          <table class="rows">
            <thead>
              <tr>
                <th>Когда</th><th>Кто</th><th>Проект</th>
                <th>Формат</th><th>Длина</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in sessions" :key="s.id">
                <td class="faint">{{ humanDate(s.started_at) }}</td>
                <td>{{ s.login }}</td>
                <td>{{ s.project }}</td>
                <td class="faint">{{ kindWord(s.kind) }}</td>
                <td class="faint">{{ s.duration_s ? humanDuration(s.duration_s) : '—' }}</td>
                <td style="text-align:right">
                  <button class="ghost sm" :disabled="busy === s.user_id"
                          @click="enter(s.user_id, '/session/' + s.id)">
                    Открыть и послушать
                  </button>
                </td>
              </tr>
              <tr v-if="!sessions.length">
                <td colspan="6" class="faint" style="padding:14px">Собесов пока нет.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>`,
};
